import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from './AuthContext';
import { useDashboardScope } from './DashboardScopeContext';

const CACHE_TTL_MS = 5 * 60 * 1000;
const isCypressTestRun = typeof window !== 'undefined' && Boolean(window.Cypress);

const normalizeRevenueForecastPayload = (rawForecast = {}) => {
    const projected = rawForecast.projected_revenue ?? rawForecast.total_projected_revenue ?? null;
    const sourceComponents = rawForecast.components || {};
    const totalForecast = sourceComponents.total_forecast ?? projected;
    const components = totalForecast === null && projected === null
        ? null
        : {
            total_forecast: totalForecast ?? projected,
            scheduled_billings: sourceComponents.scheduled_billings ?? rawForecast.scheduled_billings ?? null,
            wip_velocity: sourceComponents.wip_velocity ?? rawForecast.wip_velocity ?? null,
            actual_to_date: sourceComponents.actual_to_date ?? rawForecast.base_revenue ?? null,
        };
    const confidenceLevel = rawForecast.confidence_intervals?.confidence_level;
    const confidence = rawForecast.confidence ?? (
        confidenceLevel === null || confidenceLevel === undefined
            ? null
            : {
                score: Number(confidenceLevel) > 1
                    ? Number(confidenceLevel)
                    : Number(confidenceLevel) * 100,
                level: Number(confidenceLevel) >= 0.8 || Number(confidenceLevel) >= 80
                    ? 'high'
                    : Number(confidenceLevel) >= 0.6 || Number(confidenceLevel) >= 60
                        ? 'medium'
                        : 'low',
            }
    );

    return {
        projected_revenue: projected,
        components,
        confidence,
        confidence_intervals: rawForecast.confidence_intervals || null,
        trend_analysis: rawForecast.trend_analysis || { best_algorithm: rawForecast.best_algorithm || 'Ensemble' },
    };
};

const FinancialEngineContext = createContext(null);

export const useFinancialEngine = () => {
    const context = useContext(FinancialEngineContext);
    if (!context) {
        throw new Error('useFinancialEngine must be used within FinancialEngineProvider');
    }
    return context;
};

export const FinancialEngineProvider = ({ children }) => {
    const { user, token, loading: authLoading } = useAuth();
    const { dateRange, portfolioApiParams } = useDashboardScope();
    const [profitabilityData, setProfitabilityData] = useState(null);
    const [revenueForecasting, setRevenueForecasting] = useState(null);
    const [financialMetrics, setFinancialMetrics] = useState(null);
    const [billRateData, setBillRateData] = useState(null);
    const [backlogData, setBacklogData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [realizationData, setRealizationData] = useState(null);
    const [wipData, setWipData] = useState(null);
    const [executiveSummary, setExecutiveSummary] = useState(null);
    const [executiveSummaryKpiMetadata, setExecutiveSummaryKpiMetadata] = useState(null);
    const [executiveSummaryQuality, setExecutiveSummaryQuality] = useState(null);

    const requestRef = useRef(null);
    const lastFetchRef = useRef(0);
    const hasDataRef = useRef(false);
    const lastDateRangeRef = useRef('{}');
    const refreshInFlightRef = useRef({ key: null, promise: null });
    const refreshRequestSeqRef = useRef(0);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const handleApiError = useCallback((err, operation) => {
        console.error(`Financial engine ${operation} failed:`, err);
        if (err.response?.status === 403) {
            setError(`Access denied for ${operation}. You may not have sufficient permissions to view financial data.`);
            return;
        }
        // Keep showing loaded KPIs when a supplementary fetch fails after dashboard-summary succeeded.
        if (hasDataRef.current) {
            console.warn(
                `Financial engine ${operation} failed after core dashboard data loaded; error not surfaced to UI.`,
                err
            );
            return;
        }
        let errorMessage = `Failed to ${operation}`;
        if (err.response?.data?.detail) {
            errorMessage += `: ${err.response.data.detail}`;
        } else if (err.response?.data?.error) {
            errorMessage += `: ${err.response.data.error}`;
        } else if (err.message) {
            errorMessage += `: ${err.message}`;
        }
        setError(errorMessage);
    }, []);

    const buildDateParams = useCallback((options = {}) => {
        const startDate = options.start_date || dateRange?.start_date;
        const endDate = options.end_date || dateRange?.end_date;
        if (!startDate || !endDate) {
            return {};
        }
        const params = {
            ...portfolioApiParams,
            start_date: startDate,
            end_date: endDate,
        };
        if (options.project_id) {
            params.project_id = options.project_id;
            delete params.status;
        }
        if (options.status) {
            params.status = options.status;
        }
        Object.keys(params).forEach((key) => {
            if (params[key] === undefined || params[key] === null || params[key] === '') {
                delete params[key];
            }
        });
        return params;
    }, [dateRange?.end_date, dateRange?.start_date, portfolioApiParams]);

    const normalizePortfolioHealthScore = useCallback((score) => {
        const normalizedScore = Number(score);
        if (!Number.isFinite(normalizedScore)) {
            return null;
        }
        return normalizedScore > 1 ? normalizedScore / 100 : normalizedScore;
    }, []);

    const mapPortfolioHealthPayload = useCallback((rawData = {}) => {
        const portfolioRisk = rawData.portfolio_risk_assessment || {};
        const projectPerformanceRanking = rawData.project_performance_ranking || [];
        const totalRevenue = rawData.total_revenue ?? null;
        const netProfit = rawData.net_profit ?? null;
        const totalCosts = rawData.total_costs ?? (
            totalRevenue !== null && netProfit !== null
                ? Number(totalRevenue) - Number(netProfit)
                : null
        );

        return {
            total_revenue: totalRevenue,
            total_portfolio_value: rawData.total_portfolio_value ?? totalRevenue ?? null,
            revenue_recognized: rawData.revenue_recognized ?? null,
            net_profit: netProfit,
            total_costs: totalCosts,
            accounts_receivable: rawData.accounts_receivable ?? null,
            cash_flow: rawData.cash_flow ?? null,
            portfolio_profitability: rawData.portfolio_profitability ?? rawData.aggregate_profit_margin ?? null,
            aggregate_profit_margin: rawData.aggregate_profit_margin ?? rawData.portfolio_profitability ?? null,
            profitable_projects: rawData.profitable_projects ?? null,
            total_projects: rawData.total_projects_analyzed ?? rawData.total_projects ?? null,
            portfolio_health_score: normalizePortfolioHealthScore(rawData.portfolio_health_score ?? null),
            risk_level: portfolioRisk.risk_level || 'unknown',
            risk_score: portfolioRisk.risk_score ?? null,
            project_performance_ranking: projectPerformanceRanking
        };
    }, [normalizePortfolioHealthScore]);

    const mapPortfolioHealthMetrics = useCallback((rawData = {}) => ({
        burn_rate: rawData.burn_rate ?? null,
        budget_utilization: rawData.budget_utilization ?? null,
        completion_percentage: rawData.completion_percentage ?? null,
        variance_fraction: rawData.variance_fraction ?? null
    }), []);

    const buildExecutiveSummaryQuality = useCallback((kpiMetadata) => {
        if (!kpiMetadata || typeof kpiMetadata !== 'object') {
            return null;
        }

        const warnings = [];
        const requiredData = new Set();
        const metrics = {};

        Object.entries(kpiMetadata).forEach(([metric, metadata]) => {
            const dataQuality = metadata?.data_quality;
            if (!dataQuality || typeof dataQuality !== 'object') {
                return;
            }

            const calculationComplete = dataQuality.calculation_complete === true;
            metrics[metric] = calculationComplete ? 'ok' : 'insufficient_data';

            if (!calculationComplete) {
                (dataQuality.warnings || [])
                    .filter((item) => typeof item === 'string')
                    .forEach((item) => warnings.push(item));
                (dataQuality.required_data || [])
                    .filter((item) => typeof item === 'string')
                    .forEach((item) => requiredData.add(item));
            }
        });

        const warningSeen = new Set();
        const uniqueWarnings = [];
        warnings.forEach((item) => {
            const trimmed = item.trim();
            if (!trimmed || warningSeen.has(trimmed)) {
                return;
            }
            warningSeen.add(trimmed);
            uniqueWarnings.push(trimmed);
        });

        return {
            calculation_complete: Object.values(metrics).every((value) => value === 'ok'),
            warnings: uniqueWarnings,
            required_data: Array.from(requiredData),
            metrics,
        };
    }, []);

    const getPortfolioHealth = useCallback(async (opts = {}) => {
        if (requestRef.current && !opts.batch) return;
        try {
            if (!opts.batch) {
                setLoading(true);
                clearError();
            }
            requestRef.current = 'portfolio-health';
            const response = await apiClient.get('financial-engine/portfolio-health/', {
                params: buildDateParams(opts),
            });
            if (response.data.success) {
                const rawData = response.data.data;
                setProfitabilityData(mapPortfolioHealthPayload(rawData));
                setFinancialMetrics(mapPortfolioHealthMetrics(rawData));
            } else {
                throw new Error(response.data.message || 'Unknown error');
            }
        } catch (err) {
            if (!opts.batch) handleApiError(err, 'fetch portfolio health');
            setProfitabilityData(null);
            hasDataRef.current = false;
            if (opts.batch) throw err;
        } finally {
            if (!opts.batch) setLoading(false);
            requestRef.current = null;
        }
    }, [buildDateParams, clearError, handleApiError, mapPortfolioHealthMetrics, mapPortfolioHealthPayload]);

    const getProjectProfitability = useCallback(async (projectId) => {
        if (!projectId) {
            setError('Invalid project ID provided');
            return;
        }
        try {
            setLoading(true);
            clearError();
            const response = await apiClient.get(`financial-engine/profitability/${projectId}/`);
            if (response.data.success) return response.data.data;
            throw new Error(response.data.message || 'Unknown error');
        } catch (err) {
            handleApiError(err, 'fetch project profitability');
            return null;
        } finally {
            setLoading(false);
        }
    }, [clearError, handleApiError]);

    const generateRevenueForecast = useCallback(async (forecastParams = {}, opts = {}) => {
        try {
            if (!opts.batch) {
                setLoading(true);
                clearError();
            }
            const params = { timeframe: 90, confidence_level: 0.85, ...forecastParams };
            const response = await apiClient.post('financial-engine/revenue-forecast/', params);
            if (response.data.success) {
                const rawData = response.data;
                const transformedData = normalizeRevenueForecastPayload(rawData);
                setRevenueForecasting(transformedData);
                return transformedData;
            }
            throw new Error(response.data.message || 'Unknown error');
        } catch (err) {
            if (!opts.batch) handleApiError(err, 'generate revenue forecast');
            setRevenueForecasting(null);
            if (opts.batch) throw err;
            return null;
        } finally {
            if (!opts.batch) setLoading(false);
        }
    }, [clearError, handleApiError]);

    const getFinancialMetrics = useCallback(async (projectId) => {
        if (!projectId) {
            setError('Invalid project ID provided');
            return;
        }
        try {
            setLoading(true);
            clearError();
            const response = await apiClient.get(`financial-engine/financial-metrics/${projectId}/`);
            if (response.data.success) {
                setFinancialMetrics(response.data.data);
                return response.data.data;
            }
            throw new Error(response.data.message || 'Unknown error');
        } catch (err) {
            handleApiError(err, 'fetch financial metrics');
            setFinancialMetrics(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, [clearError, handleApiError]);

    const getBudgetVariance = useCallback(async (projectId) => {
        if (!projectId) {
            setError('Invalid project ID provided');
            return;
        }
        try {
            setLoading(true);
            clearError();
            const response = await apiClient.get(`financial-engine/budget-variance/${projectId}/`);
            if (response.data.success) return response.data.data;
            throw new Error(response.data.message || 'Unknown error');
        } catch (err) {
            handleApiError(err, 'fetch budget variance');
            return null;
        } finally {
            setLoading(false);
        }
    }, [clearError, handleApiError]);

    const getBillRateData = useCallback(async (opts = {}) => {
        try {
            if (!opts.batch) {
                setLoading(true);
                clearError();
            }
            const params = buildDateParams(opts);
            const response = await apiClient.get('financial-engine/bill-rates/', { params });
            setBillRateData(response.data);
            return response.data;
        } catch (err) {
            if (!opts.batch) handleApiError(err, 'fetch bill rate data');
            setBillRateData(null);
            if (opts.batch) throw err;
            return null;
        } finally {
            if (!opts.batch) setLoading(false);
        }
    }, [buildDateParams, clearError, handleApiError]);

    const getBacklogData = useCallback(async (opts = {}) => {
        try {
            if (!opts.batch) {
                setLoading(true);
                clearError();
            }
            const params = buildDateParams(opts);
            const response = await apiClient.get('financial-engine/backlog/', { params });
            if (response.data.success) {
                setBacklogData(response.data.data);
                return response.data.data;
            }
            throw new Error(response.data.message || 'Unknown error');
        } catch (err) {
            if (!opts.batch) handleApiError(err, 'fetch backlog data');
            setBacklogData(null);
            if (opts.batch) throw err;
            return null;
        } finally {
            if (!opts.batch) setLoading(false);
        }
    }, [buildDateParams, clearError, handleApiError]);

    const getRealizationData = useCallback(async (projectId, opts = {}) => {
        try {
            if (!opts.batch) {
                setLoading(true);
                clearError();
            }
            const url = projectId ? `financial-engine/realization/?project_id=${projectId}` : 'financial-engine/realization/';
            const params = buildDateParams(opts);
            const response = await apiClient.get(url, { params });
            if (response.data.success) {
                setRealizationData(response.data.data);
                return response.data.data;
            }
            throw new Error(response.data.message || 'Unknown error');
        } catch (err) {
            if (!opts.batch) handleApiError(err, 'fetch realization data');
            setRealizationData(null);
            if (opts.batch) throw err;
            return null;
        } finally {
            if (!opts.batch) setLoading(false);
        }
    }, [buildDateParams, clearError, handleApiError]);

    const getWipData = useCallback(async (projectId, opts = {}) => {
        try {
            if (!opts.batch) {
                setLoading(true);
                clearError();
            }
            const url = projectId ? `financial-engine/wip/?project_id=${projectId}` : 'financial-engine/wip/';
            const params = buildDateParams(opts);
            const response = await apiClient.get(url, { params });
            if (response.data.success) {
                setWipData(response.data.data);
                return response.data.data;
            }
            throw new Error(response.data.message || 'Unknown error');
        } catch (err) {
            if (!opts.batch) handleApiError(err, 'fetch wip data');
            setWipData(null);
            if (opts.batch) throw err;
            return null;
        } finally {
            if (!opts.batch) setLoading(false);
        }
    }, [buildDateParams, clearError, handleApiError]);

    const getExecutiveSummary = useCallback(async (opts = {}) => {
        try {
            if (!opts.batch) {
                setLoading(true);
                clearError();
            }
            const response = await apiClient.get('financial-engine/executive-summary/', { params: buildDateParams(opts) });
            if (response.data.success) {
                setExecutiveSummary(response.data.data);
                const kpiMetadata = response.data.data?.kpi_metadata;
                setExecutiveSummaryKpiMetadata(kpiMetadata ?? null);
                setExecutiveSummaryQuality(buildExecutiveSummaryQuality(kpiMetadata));
                return response.data.data;
            }
            throw new Error(response.data.message || 'Unknown error');
        } catch (err) {
            if (!opts.batch) handleApiError(err, 'fetch executive summary');
            setExecutiveSummary(null);
            setExecutiveSummaryKpiMetadata(null);
            setExecutiveSummaryQuality(null);
            if (opts.batch) throw err;
            return null;
        } finally {
            if (!opts.batch) setLoading(false);
        }
    }, [buildDateParams, buildExecutiveSummaryQuality, clearError, handleApiError]);

    const refreshFinancialData = useCallback(async (forceRefresh = false, opts = {}) => {
        const dateParams = buildDateParams(opts);
        const dateParamsKey = JSON.stringify(dateParams);
        const dateRangeChanged = dateParamsKey !== lastDateRangeRef.current;
        const cacheValid = !isCypressTestRun
            && !forceRefresh
            && hasDataRef.current
            && (Date.now() - lastFetchRef.current) < CACHE_TTL_MS
            && !dateRangeChanged;
        if (cacheValid) return;

        if (!isCypressTestRun && refreshInFlightRef.current.promise && refreshInFlightRef.current.key === dateParamsKey) {
            return refreshInFlightRef.current.promise;
        }

        const requestId = ++refreshRequestSeqRef.current;
        const dashboardSummaryParams = { params: dateParams };
        const executiveSummaryParams = { params: dateParams };

        const doRefresh = (async () => {
            const isLatestRequest = () => requestId === refreshRequestSeqRef.current;
            try {
                setLoading(true);
                clearError();
                // Parallel HTTP calls; apply each response as soon as it arrives (do not wait on the
                // slower peer — Promise.all deferred profitability paint until both finished).
                const applyDashboardSummary = (summaryResp) => {
                    if (!summaryResp.data?.success || !summaryResp.data?.data) {
                        return;
                    }
                    const d = summaryResp.data.data;
                    if (d.portfolio_health) {
                        const raw = d.portfolio_health;
                        const mapped = mapPortfolioHealthPayload(raw);
                        setProfitabilityData((current) => ({
                            ...(current || {}),
                            ...mapped,
                            portfolio_health_score: mapped.portfolio_health_score ?? current?.portfolio_health_score,
                            cash_flow: mapped.cash_flow ?? current?.cash_flow,
                            total_revenue: mapped.total_revenue,
                            net_profit: mapped.net_profit,
                            revenue_recognized: mapped.revenue_recognized,
                        }));
                        setFinancialMetrics((current) => ({
                            ...(current || {}),
                            ...mapPortfolioHealthMetrics(raw),
                        }));
                    }
                    if (d.revenue_forecast) {
                        setRevenueForecasting(normalizeRevenueForecastPayload(d.revenue_forecast));
                    }
                    if (d.bill_rates) setBillRateData(d.bill_rates);
                    if (d.backlog) setBacklogData(d.backlog?.data ?? d.backlog);
                    if (d.realization) setRealizationData(d.realization?.data ?? d.realization);
                    if (d.wip) setWipData(d.wip?.data ?? d.wip);
                    lastFetchRef.current = Date.now();
                    hasDataRef.current = true;
                    lastDateRangeRef.current = dateParamsKey;
                };

                const summaryPromise = apiClient
                    .get('financial-engine/dashboard-summary/', dashboardSummaryParams)
                    .then((summaryResp) => {
                        if (!isLatestRequest()) return;
                        applyDashboardSummary(summaryResp);
                    });

                const execPromise = apiClient
                    .get('financial-engine/executive-summary/', executiveSummaryParams)
                    .then((execResp) => {
                        if (!isLatestRequest()) return;
                        if (execResp.data?.success && execResp.data?.data) {
                            setExecutiveSummary(execResp.data.data);
                            const kpiMetadata = execResp.data.data?.kpi_metadata;
                            setExecutiveSummaryKpiMetadata(kpiMetadata ?? null);
                            setExecutiveSummaryQuality(buildExecutiveSummaryQuality(kpiMetadata));
                        } else {
                            setExecutiveSummary(null);
                            setExecutiveSummaryKpiMetadata(null);
                            setExecutiveSummaryQuality(null);
                        }
                    });

                const portfolioHealthPromise = apiClient
                    .get('financial-engine/portfolio-health/', dashboardSummaryParams)
                    .then((phResp) => {
                        if (!isLatestRequest()) return;
                        if (!phResp.data?.success || !phResp.data?.data) {
                            return;
                        }
                        const phMapped = mapPortfolioHealthPayload(phResp.data.data);
                        setProfitabilityData((current) => ({
                            ...(current || {}),
                            ...phMapped,
                            portfolio_health_score: phMapped.portfolio_health_score ?? current?.portfolio_health_score,
                            cash_flow: phMapped.cash_flow ?? current?.cash_flow,
                            // Never retain stale totals when a fresh portfolio-health response arrives.
                            total_revenue: phMapped.total_revenue,
                            net_profit: phMapped.net_profit,
                            revenue_recognized: phMapped.revenue_recognized,
                        }));
                        setFinancialMetrics((current) => ({
                            ...(current || {}),
                            ...mapPortfolioHealthMetrics(phResp.data.data),
                        }));
                    });

                const settled = await Promise.allSettled([
                    summaryPromise,
                    execPromise,
                    portfolioHealthPromise,
                ]);
                if (!isLatestRequest()) return;

                if (settled[0].status === 'rejected') {
                    throw settled[0].reason;
                }
                // Executive summary is supplementary; do not fail the refresh when dashboard-summary succeeded.
                if (settled[1].status === 'rejected') {
                    console.warn(
                        'Executive summary fetch failed; continuing with dashboard-summary data:',
                        settled[1].reason
                    );
                }
            } catch (err) {
                const batchOpts = { batch: true };
                const batchDateOpts = { ...batchOpts, ...dateParams };
                const results = await Promise.allSettled([
                    getPortfolioHealth({ ...batchOpts, ...dateParams }),
                    generateRevenueForecast({}, batchOpts),
                    getBillRateData({ ...batchDateOpts }),
                    getBacklogData({ ...batchDateOpts }),
                    getRealizationData(undefined, batchDateOpts),
                    getWipData(undefined, batchDateOpts),
                    getExecutiveSummary(batchDateOpts),
                ]);
                if (!isLatestRequest()) return;
                const rejected = results.filter((r) => r.status === 'rejected');
                if (rejected.length > 0 && rejected.length === results.length) {
                    handleApiError(rejected[0].reason, 'refresh financial data');
                } else if (rejected.length < results.length) {
                    lastFetchRef.current = Date.now();
                    hasDataRef.current = true;
                    lastDateRangeRef.current = dateParamsKey;
                }
            } finally {
                if (!isLatestRequest()) return;
                setLoading(false);
                if (refreshInFlightRef.current.key === dateParamsKey) {
                    refreshInFlightRef.current = {
                        key: null,
                        promise: null
                    };
                }
            }
        });
        refreshInFlightRef.current = {
            key: dateParamsKey,
            promise: doRefresh()
        };
        return refreshInFlightRef.current.promise;
    }, [buildDateParams, buildExecutiveSummaryQuality, getPortfolioHealth, generateRevenueForecast, getBillRateData, getBacklogData, getRealizationData, getWipData, getExecutiveSummary, clearError, handleApiError, mapPortfolioHealthPayload, mapPortfolioHealthMetrics]);

    // Warm portfolio/executive payloads after auth using global dashboard scope.
    // Role allowlist: only admin/director/finance can call the executive-level APIs
    // (IsFinanceOrAbove permission class). All other roles get 403, so we skip early
    // to prevent a cascade of unhandled 403 errors that crash employee/IC views.
    const FINANCIAL_ENGINE_ALLOWED_ROLES = ['admin', 'director', 'manager', 'finance', 'accountant', 'cfo'];
    useEffect(() => {
        if (isCypressTestRun || authLoading || !token || !user) {
            return undefined;
        }
        // Skip for roles without executive-level API access. Superusers are always allowed.
        if (!user?.is_superuser && !FINANCIAL_ENGINE_ALLOWED_ROLES.includes(user?.role)) {
            return undefined;
        }
        if (!dateRange?.start_date || !dateRange?.end_date) {
            return undefined;
        }
        refreshFinancialData(false, {
            start_date: dateRange.start_date,
            end_date: dateRange.end_date,
        }).catch(() => {
            // Handled via context error state; avoid uncaught rejections on public routes.
        });
        return undefined;
    }, [
        refreshFinancialData,
        token,
        user,
        authLoading,
        dateRange?.start_date,
        dateRange?.end_date,
        portfolioApiParams,
    ]);

    const value = {
        profitabilityData,
        revenueForecasting,
        financialMetrics,
        billRateData,
        backlogData,
        realizationData,
        wipData,
        executiveSummary,
        executiveSummaryKpiMetadata,
        executiveSummaryQuality,
        loading,
        error,
        refreshFinancialData,
        generateRevenueForecast,
        getProjectProfitability,
        getPortfolioHealth,
        getFinancialMetrics,
        getBudgetVariance,
        getBillRateData,
        getBacklogData,
        getRealizationData,
        getWipData,
        getExecutiveSummary,
        clearError
    };

    return (
        <FinancialEngineContext.Provider value={value}>
            {children}
        </FinancialEngineContext.Provider>
    );
};
